const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

async function apiFetch<T>(path: string, token: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...options?.headers,
    },
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.error || `API error: ${res.status}`);
  }
  return json.data;
}

export function createApiClient(token: string) {
  return {
    listScans: (params?: { status?: string; limit?: number; nextToken?: string }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.nextToken) qs.set('nextToken', params.nextToken);
      const query = qs.toString();
      return apiFetch<any>(`/admin/scans${query ? `?${query}` : ''}`, token);
    },
    getScanDetail: (scanSessionId: string) => {
      return apiFetch<any>(`/admin/scans/${scanSessionId}`, token);
    },
    submitCompReview: (review: {
      scanSessionId: string;
      compUrl: string;
      relevance: 'relevant' | 'irrelevant' | 'marginal';
      notes?: string;
      candidateBrand?: string;
      candidateModel?: string;
      candidateCategory?: string;
      compTitle?: string;
      compPrice?: number;
      compSource?: string;
      compPriceType?: string;
      relevanceScore?: number;
    }) => {
      return apiFetch<any>('/admin/comp-reviews', token, {
        method: 'POST',
        body: JSON.stringify(review),
      });
    },
    listCompReviews: (params?: { scanSessionId?: string; relevance?: string; limit?: number; nextToken?: string }) => {
      const qs = new URLSearchParams();
      if (params?.scanSessionId) qs.set('scanSessionId', params.scanSessionId);
      if (params?.relevance) qs.set('relevance', params.relevance);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.nextToken) qs.set('nextToken', params.nextToken);
      const query = qs.toString();
      return apiFetch<any>(`/admin/comp-reviews${query ? `?${query}` : ''}`, token);
    },
    getCompReviewStats: () => {
      return apiFetch<any>('/admin/comp-reviews/stats', token);
    },

    // Beta requests
    listBetaRequests: (params?: { status?: string; limit?: number; lastEvaluatedKey?: string }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.lastEvaluatedKey) qs.set('lastEvaluatedKey', params.lastEvaluatedKey);
      const query = qs.toString();
      return apiFetch<any>(`/admin/beta-requests${query ? `?${query}` : ''}`, token);
    },
    updateBetaRequest: (requestId: string, body: { status: string; notes?: string }) => {
      return apiFetch<any>(`/admin/beta-requests/${requestId}`, token, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },
    deleteBetaRequest: (requestId: string) => {
      return apiFetch<any>(`/admin/beta-requests/${requestId}`, token, {
        method: 'DELETE',
      });
    },
    convertBetaRequest: (requestId: string) => {
      return apiFetch<any>(`/admin/beta-requests/${requestId}/convert`, token, {
        method: 'POST',
      });
    },
    bulkApproveBetaRequests: (requestIds: string[]) => {
      return apiFetch<any>('/admin/beta-requests/bulk-approve', token, {
        method: 'POST',
        body: JSON.stringify({ requestIds }),
      });
    },

    // Users
    listUsers: (params?: { search?: string; tier?: string; limit?: number; nextToken?: string }) => {
      const qs = new URLSearchParams();
      if (params?.search) qs.set('search', params.search);
      if (params?.tier) qs.set('tier', params.tier);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.nextToken) qs.set('nextToken', params.nextToken);
      const query = qs.toString();
      return apiFetch<any>(`/admin/users${query ? `?${query}` : ''}`, token);
    },
    getUserDetail: (userId: string) => {
      return apiFetch<any>(`/admin/users/${userId}`, token);
    },
    listUserScans: (userId: string, params?: { limit?: number; nextToken?: string }) => {
      const qs = new URLSearchParams();
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.nextToken) qs.set('nextToken', params.nextToken);
      const query = qs.toString();
      return apiFetch<any>(`/admin/users/${userId}/scans${query ? `?${query}` : ''}`, token);
    },
    updateUser: (userId: string, body: { subscriptionTier?: string; analysesLimit?: number }) => {
      return apiFetch<any>(`/admin/users/${userId}`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },

    // FinOps
    getFinopsUsage: (period: string = 'current_month', startDate?: string, endDate?: string) => {
      const qs = new URLSearchParams({ period });
      if (startDate) qs.set('startDate', startDate);
      if (endDate) qs.set('endDate', endDate);
      return apiFetch<any>(`/admin/finops/usage?${qs}`, token);
    },
    getFinopsCosts: (period: string = 'current_month', startDate?: string, endDate?: string) => {
      const qs = new URLSearchParams({ period });
      if (startDate) qs.set('startDate', startDate);
      if (endDate) qs.set('endDate', endDate);
      return apiFetch<any>(`/admin/finops/costs?${qs}`, token);
    },

    // Prompt Overrides
    listPromptOverrides: () => {
      return apiFetch<any>('/admin/prompt-overrides', token);
    },
    getPromptOverride: (categoryId: string, promptType: string) => {
      return apiFetch<any>(`/admin/prompt-overrides/${categoryId}?promptType=${promptType}`, token);
    },
    savePromptOverride: (body: {
      categoryId: string;
      promptType: string;
      promptText: string;
      status?: string;
      notes?: string;
    }) => {
      return apiFetch<any>('/admin/prompt-overrides', token, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    deletePromptOverride: (categoryId: string, promptType: string) => {
      return apiFetch<any>(`/admin/prompt-overrides/${categoryId}?promptType=${promptType}`, token, {
        method: 'DELETE',
      });
    },
    getCodePrompts: (categoryId?: string) => {
      const qs = categoryId ? `?categoryId=${categoryId}` : '';
      return apiFetch<any>(`/admin/code-prompts${qs}`, token);
    },
  };
}
